import { useState, useEffect, useCallback } from 'react'
import { Business, BusinessStage } from '@/lib/db/schema'
import { UpdateBusinessInput } from '@/lib/validation/business'

export function useBusiness(businessId: string | undefined) {
  const [business, setBusiness] = useState<Business | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchBusiness = useCallback(async () => {
    if (!businessId) {
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)
    try {
      const res = await fetch(`/api/businesses/${businessId}`)
      const json = await res.json() as any
      if (res.ok && json.success) {
        setBusiness(json.data)
      } else {
        setError(json.error?.message || 'Failed to fetch business')
      }
    } catch (err: any) {
      setError(err.message || 'Network error')
    } finally {
      setIsLoading(false)
    }
  }, [businessId])

  useEffect(() => {
    fetchBusiness()
  }, [fetchBusiness])

  const updateBusiness = async (payload: UpdateBusinessInput) => {
    if (!businessId) return { success: false, error: 'No ID' }
    
    const res = await fetch(`/api/businesses/${businessId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    const json = await res.json() as any
    if (res.ok && json.success) {
      setBusiness(json.data)
      return { success: true, data: json.data as Business }
    }
    return { success: false, error: json.error?.message || 'Failed to update business', fields: json.error?.fields }
  }

  const updateStage = async (stage: BusinessStage) => {
    return updateBusiness({ stage })
  }

  return { business, isLoading, error, refetch: fetchBusiness, updateBusiness, updateStage }
}
